(() => {
  "use strict";

  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const idle = (fn) => {
    if ("requestIdleCallback" in window) window.requestIdleCallback(fn, { timeout: 2000 });
    else window.setTimeout(fn, 1);
  };

  const debounce = (fn, wait = 140) => {
    let t;
    return (...args) => {
      window.clearTimeout(t);
      t = window.setTimeout(() => fn.apply(null, args), wait);
    };
  };

  function trackEvent(name, params = {}) {
    try {
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: name, ...params });
        return;
      }
      if (typeof window.gtag === "function") {
        window.gtag("event", name, params);
      }
    } catch (_) {}
  }

  function slugify(str) {
    return String(str || "")
      .toLowerCase()
      .trim()
      .replace(/['"]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function initClickTracking() {
    document.addEventListener(
      "click",
      (e) => {
        const el = e.target && e.target.closest ? e.target.closest("[data-ga-event]") : null;
        if (!el) return;
        trackEvent(el.getAttribute("data-ga-event"), {
          event_label: el.getAttribute("data-ga-label") || "",
          event_category: "article_engagement",
          article_title: window.ARIEL_RIDER?.article?.title || ""
        });
      },
      { passive: true }
    );
  }

  function generateToC() {
    const body = qs("#article-body");
    const container = qs("#ar-toc-container");
    if (!body || !container) return;

    const headers = qsa("h2", body);
    if (headers.length < 2) return;

    const seen = new Map();
    headers.forEach((h, idx) => {
      if (h.id) return;
      const base = slugify(h.textContent) || `section-${idx}`;
      const count = (seen.get(base) || 0) + 1;
      seen.set(base, count);
      h.id = count === 1 ? base : `${base}-${count}`;
    });

    const details = document.createElement("details");
    details.className = "ar-toc";
    details.open = true;

    const summary = document.createElement("summary");
    summary.textContent = "Table of Contents";
    details.appendChild(summary);

    const list = document.createElement("ul");
    list.className = "ar-toc-list";

    headers.forEach((h) => {
      const li = document.createElement("li");
      const a = document.createElement("a");
      a.href = `#${h.id}`;
      a.textContent = h.textContent || "Section";
      li.appendChild(a);
      list.appendChild(li);
    });

    container.innerHTML = "";
    details.appendChild(list);
    container.appendChild(details);
  }

  function initLightbox() {
    const body = qs("#article-body");
    const box = qs("#ar-lightbox") || qs("#lightbox");
    if (!body || !box) return;

    const imgEl = qs("img", box);
    const closeBtn = qs(".ar-lightbox-close", box);

    const open = (src, alt) => {
      imgEl.src = src;
      imgEl.alt = alt || "";
      box.removeAttribute("hidden");
      box.classList.add("active");
      document.documentElement.classList.add("ar-noscroll");
      trackEvent("image_lightbox_open", { article_title: window.ARIEL_RIDER?.article?.title || "" });
    };

    const close = () => {
      box.classList.remove("active");
      document.documentElement.classList.remove("ar-noscroll");
      window.setTimeout(() => box.setAttribute("hidden", ""), 200);
    };

    body.addEventListener("click", (e) => {
      const img = e.target && e.target.closest ? e.target.closest("img") : null;
      if (!img) return;

      const parentLink = img.closest("a");
      const src = parentLink && parentLink.href ? parentLink.href : img.currentSrc || img.src;
      open(src, img.alt);
    });

    box.addEventListener("click", (e) => {
      if (e.target === box || e.target === closeBtn) close();
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && box.classList.contains("active")) close();
    });
  }

  function initCopyLink() {
    const btn = qs(".ar-copy-link");
    const toast = qs("#ar-toast");
    if (!btn) return;

    const showToast = () => {
      if (!toast) return;
      toast.classList.add("is-visible");
      window.setTimeout(() => toast.classList.remove("is-visible"), 1600);
    };

    btn.addEventListener("click", async () => {
      const url = btn.getAttribute("data-url") || window.location.href;

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url);
        } else {
          const t = document.createElement("textarea");
          t.value = url;
          t.setAttribute("readonly", "");
          t.style.position = "absolute";
          t.style.left = "-9999px";
          document.body.appendChild(t);
          t.select();
          document.execCommand("copy");
          document.body.removeChild(t);
        }

        showToast();
        trackEvent("share_click", {
          method: "copy_link",
          article_title: window.ARIEL_RIDER?.article?.title || ""
        });
      } catch (_) {}
    });
  }

  function initRangeMini() {
    const minis = qsa('[data-ar-mini="range"]');
    if (!minis.length) return;

    const g = 9.80665;
    const lbToKg = 0.45359237;

    const clamp = (n, a, b) => (n < a ? a : n > b ? b : n);

    const parseMaybeTenths = (n) => {
      const v = Number(n);
      if (!Number.isFinite(v) || v <= 0) return NaN;
      // If they store 195 to mean 19.5 Wh/mi, decode here.
      return v >= 100 ? v / 10 : v;
    };

    minis.forEach((root) => {
      const id = root.getAttribute("data-block-id");
      if (!id) return;

      const BAT1 = Number(root.dataset.bat1 || 1152);
      const BAT2 = Number(root.dataset.bat2 || 1152);
      const BIKE_LB = Number(root.dataset.bike || 90);

      const REF_RIDER_LB = Number(root.dataset.ref || 180);
      const BASE_WHPM = parseMaybeTenths(root.dataset.base || 195);
      const MASS_EXP = Number(root.dataset.exp || 35) / 100;

      const F_E = Number(root.dataset.fE || 90) / 100;
      const F_M = Number(root.dataset.fM || 100) / 100;
      const F_T = Number(root.dataset.fT || 115) / 100;

      const BLO = Number(root.dataset.bandLow || 90) / 100;
      const BHI = Number(root.dataset.bandHigh || 110) / 100;
      const CAP = Number(root.dataset.capMi || 150);

      const elW = qs(`#arMiniW-${id}`, root);
      const elE = qs(`#arMiniE-${id}`, root);
      const elA = qs(`#arMiniA-${id}`, root);
      const elMi = qs(`#arMiniMi-${id}`, root);
      const elBd = qs(`#arMiniBand-${id}`, root);
      const elV = qs(`#arMiniV-${id}`, root);
      const elAh = qs(`#arMiniAh-${id}`, root);
      const elWh = qs(`#arMiniWh-${id}`, root);
      const battRadios = qsa(`input[name="arMiniBatt-${id}"]`, root);

      if (!elW || !elE || !elA || !elMi || !elBd) return;

      const assistFactor = (mode) => {
        if (mode === "economy") return Number.isFinite(F_E) ? F_E : 0.9;
        if (mode === "throttle") return Number.isFinite(F_T) ? F_T : 1.15;
        return Number.isFinite(F_M) ? F_M : 1.0;
      };

      const userWhSingle = () => {
        const wh = parseFloat(elWh && elWh.value ? elWh.value : "NaN");
        if (!Number.isNaN(wh) && wh > 0) return wh;

        const v = parseFloat(elV && elV.value ? elV.value : "NaN");
        const ah = parseFloat(elAh && elAh.value ? elAh.value : "NaN");
        if (!Number.isNaN(v) && !Number.isNaN(ah) && v > 0 && ah > 0) return v * ah;

        return BAT1;
      };

      const batteryWhTotal = () => {
        const base = userWhSingle();
        const chosen = battRadios.find((r) => r.checked)?.value || "single";
        return chosen === "dual" ? base + BAT2 : base;
      };

      const update = () => {
        const riderLb = clamp(parseFloat(elW.value || "0"), 80, 350);
        const elevFt = clamp(parseFloat(elE.value || "0"), 0, 20000);
        const assist = elA.value || "mixed";

        const capWh = batteryWhTotal();

        // Mass + climb energy
        const totalLb = riderLb + BIKE_LB;
        const massKg = totalLb * lbToKg;
        const h_m = elevFt * 0.3048;
        const climbWh = (massKg * g * h_m) / 3600;

        const usableWh = capWh - climbWh;
        if (!Number.isFinite(usableWh) || usableWh <= 0) {
          elMi.textContent = "0";
          elBd.textContent = "Elevation exceeds battery capacity. Reduce elevation or use dual battery.";
          return;
        }

        // Base consumption model (tunable)
        const baseWhpm = Number.isFinite(BASE_WHPM) && BASE_WHPM > 0 ? BASE_WHPM : 20;

        const refTotalLb = (Number.isFinite(REF_RIDER_LB) ? REF_RIDER_LB : 180) + BIKE_LB;
        const ratio = Math.max(0.5, totalLb / Math.max(1, refTotalLb));
        const weightFactor = Math.pow(ratio, Number.isFinite(MASS_EXP) ? MASS_EXP : 0.35);

        const modeFactor = assistFactor(assist);
        const whPerMile = baseWhpm * weightFactor * modeFactor;

        const miles = usableWh / Math.max(whPerMile, 0.001);

        const low = clamp(miles * BLO, 0, CAP);
        const high = clamp(miles * BHI, 0, CAP);
        const mid = clamp(miles, 0, CAP);

        elMi.textContent = mid >= CAP ? `${CAP}+` : String(Math.round(mid));
        elBd.textContent = `Range: ${Math.round(low)} to ${Math.round(high)} mi`;

        trackEvent("calculator_interaction", {
          calculator_type: "range_mini",
          assist_mode: assist,
          rider_weight_lb: riderLb,
          elevation_ft: elevFt,
          estimated_range_mi: Math.round(mid),
          battery_wh: Math.round(capWh),
          article_title: window.ARIEL_RIDER?.article?.title || ""
        });
      };

      const onInput = debounce(update, 160);
      [elW, elE, elA, elV, elAh, elWh].forEach((el) => el && el.addEventListener("input", onInput, { passive: true }));
      battRadios.forEach((r) => r.addEventListener("change", onInput, { passive: true }));

      update();
    });
  }

  function initScrollAndTimeTracking() {
    const title = window.ARIEL_RIDER?.article?.title || "";
    let maxScroll = 0;
    const milestones = [25, 50, 75, 90, 100];
    const seen = new Set();

    const onScroll = debounce(() => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop || 0;
      const scrollHeight = Math.max(1, doc.scrollHeight - window.innerHeight);
      const pct = Math.round((scrollTop / scrollHeight) * 100);

      if (pct > maxScroll) maxScroll = pct;

      milestones.forEach((m) => {
        if (pct >= m && !seen.has(m)) {
          seen.add(m);
          trackEvent("scroll_depth", { scroll_percent: m, article_title: title });
        }
      });
    }, 120);

    window.addEventListener("scroll", onScroll, { passive: true });

    [30, 60, 120, 300].forEach((t) => {
      window.setTimeout(() => {
        if (document.visibilityState === "visible") {
          trackEvent("time_on_page", { time_seconds: t, article_title: title });
        }
      }, t * 1000);
    });

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        trackEvent("article_exit", { max_scroll: maxScroll, article_title: title });
      }
    });
  }

  function init() {
    initClickTracking();
    generateToC();
    initLightbox();
    initCopyLink();
    initRangeMini();
    initScrollAndTimeTracking();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => idle(init));
  } else {
    idle(init);
  }
})();
